import { useEffect, useState } from "react";
import axios from "axios";
import { logError } from "@/lib/logger";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCountUp } from "@/hooks/useCountUp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_VISIBLE_COUNT = 50;

/**
 * Real-time count of watches registered in the registry, with a count-up that
 * plays when the section scrolls into view. Polls every 30s (re-animates on
 * change). No page refresh needed.
 */
export const RegistryCounter = () => {
  const { t, language } = useLanguage();
  const [target, setTarget] = useState(null);
  const { ref, value } = useCountUp(target, 1800);

  useEffect(() => {
    let active = true;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/stats/watches-count`);
        if (active && typeof res.data?.count === "number") setTarget(res.data.count);
      } catch (err) {
        logError("Failed to fetch watches count:", err);
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  // Below the credibility threshold, an absent counter would leave the launch
  // period with zero social proof. Show an honest founders marker instead.
  if (target === null || target < MIN_VISIBLE_COUNT) {
    return (
      <section
        data-testid="registry-founders"
        className="relative bg-paper border-y border-ink/5 py-14 md:py-32 px-6 text-center overflow-hidden"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="seal-metal mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full">
          <img
            src="/provena-monogram.png"
            alt=""
            aria-hidden="true"
            className="h-[62%] w-[62%] object-contain brightness-0 opacity-60"
            draggable="false"
          />
        </div>
        <p className="mb-3 text-xs tracking-[0.4em] uppercase text-gold-ink">
          {t("counter.foundersEyebrow")}
        </p>
        <p className="engraved font-serif text-2xl sm:text-3xl tracking-[0.12em] text-gold-gradient">
          {t("counter.foundersTitle")}
        </p>
        <p className="mx-auto mt-4 max-w-md text-sm text-ink-muted leading-relaxed">
          {t("counter.foundersText")}
        </p>
      </section>
    );
  }

  return (
    <section
      ref={ref}
      data-testid="registry-counter"
      className="relative bg-paper border-y border-ink/5 py-14 md:py-32 px-6 text-center overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <span className="mb-6 inline-flex items-center gap-2.5 text-xs tracking-[0.4em] uppercase text-gold-ink">
        <span aria-hidden="true" className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold/60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-strong" />
        </span>
        {t("counter.eyebrow")}
      </span>
      <p
        data-testid="registry-counter-value"
        className="font-serif text-6xl sm:text-7xl md:text-8xl tracking-tight text-gold-gradient tabular-nums leading-none"
      >
        {value.toLocaleString(language === "en" ? "en-US" : "fr-FR")}
      </p>
      <p className="mt-5 text-sm md:text-base tracking-[0.25em] uppercase text-ink-muted">
        {t("counter.label")}
      </p>
      <div className="mx-auto mt-8 h-px w-16 bg-gold/30" />
    </section>
  );
};

export default RegistryCounter;
